/**
 * Разбор строки AddressFull из СДЭК (через запятую) для подстановки в поля формы адреса.
 * Пример: "121552, Россия, Москва, Москва, ул. Ельнинская, 20 корп. 1"
 */
export function parseCdekAddressFull(addressFull) {
    if (!addressFull || typeof addressFull !== 'string') {
        return null;
    }
    const parts = addressFull.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) {
        return null;
    }

    if (parts.length >= 6 && /^\d{6}$/.test(parts[0])) {
        const postalCode = parts[0];
        const city = parts[3].replace(/^(г|г\.|город)\s+/i, '').trim();
        const street = parts[4];
        const house = parts.slice(5).join(', ').trim();
        return { postalCode, city, street, house };
    }

    let postalCode = '';
    if (/^\d{6}$/.test(parts[0])) {
        postalCode = parts[0];
    }

    let city = '';
    for (let i = 1; i < parts.length; i++) {
        const p = parts[i];
        if (p === 'Россия' || /^Российская Федерация/i.test(p)) {
            continue;
        }
        if (/^(г|г\.|город)\s+/i.test(p) || /^[А-Яа-яЁё\s\-]{2,}$/.test(p)) {
            const c = p.replace(/^(г|г\.|город)\s+/i, '').trim();
            if (c.length >= 2 && !/ул\.|просп|пер\.|шоссе|бульвар|наб\.|проезд|аллея|мкр|дом|д\.|корп/i.test(c)) {
                city = c;
                break;
            }
        }
    }
    if (!city && parts.length >= 4) {
        city = parts[3].replace(/^(г|г\.|город)\s+/i, '').trim();
    }

    const streetIdx = parts.findIndex((p) =>
        /ул\.|просп|пер\.|шоссе|бульвар|наб\.|проезд|аллея|мкр\.|переулок|ш\.|набережная/i.test(p));

    let street = '';
    let house = '';
    if (streetIdx >= 0) {
        street = parts[streetIdx];
        if (streetIdx + 1 < parts.length) {
            house = parts.slice(streetIdx + 1).join(', ').trim();
        }
    }

    return {
        postalCode,
        city,
        street,
        house
    };
}

/**
 * Собирает патч объекта адреса формы из данных ПВЗ (API + разбор строки).
 */
export function buildAddressFromPvz(pvz, addressFull, prevAddress = {}) {
    const postalFromApi = pvz && (pvz.postalCode || pvz.PostalCode);
    const cityFromApi = pvz && (pvz.city || pvz.City);
    const addressLine = pvz && (pvz.address || pvz.Address);

    const parsed = parseCdekAddressFull(addressFull) || {};

    // Важно: для корректной логики «ближайших ПВЗ» и расчёта тарифов
    // мы НЕ должны считать выбор ПВЗ изменением НП (город|индекс),
    // иначе createParcelForm сбрасывает shipment/deliveryPointCode.
    // Поэтому postalCode/city остаются как у исходного адреса пользователя.
    const postalCode = prevAddress.postalCode || postalFromApi || parsed.postalCode || '';
    const cityName = (prevAddress.cityName || prevAddress.city || cityFromApi || parsed.city || '').trim();
    const street = (addressLine || parsed.street || prevAddress.street || '').trim();
    const house = (parsed.house || prevAddress.house || '').trim();

    let fullHouseAddress = '';
    if (street && house) {
        fullHouseAddress = `${street}, ${house}`;
    } else if (house) {
        fullHouseAddress = house;
    } else if (addressFull) {
        fullHouseAddress = addressFull;
    } else {
        fullHouseAddress = prevAddress.fullHouseAddress || '';
    }

    const cityDisplay = prevAddress.city || (
        cityName
            ? ((cityName.startsWith('г ') || cityName.startsWith('г.')) ? cityName : `г ${cityName}`)
            : ''
    );

    return {
        ...prevAddress,
        countryCode: prevAddress.countryCode || 'RU',
        postalCode,
        city: cityDisplay,
        cityName: cityName || prevAddress.cityName,
        street,
        house,
        fullHouseAddress,
        fullName: addressFull || prevAddress.fullName,
        // geo — только из адреса до выбора ПВЗ (НП и т.д.), не координаты ПВЗ (см. useEffect списка ПВЗ в pointSection).
        geoLat: prevAddress.geoLat,
        geoLon: prevAddress.geoLon,
        id: null
    };
}

export function normalizeCityLabel(city) {
    if (!city) {
        return '';
    }

    let normalized = String(city).trim();
    if (normalized.includes(',')) {
        const parts = normalized.split(',').map((p) => p.trim()).filter(Boolean);
        const cityPart = parts.find((p) => /^(город|г\.|г)\s+/i.test(p))
            || parts.find((p) => !/^(россия|рф)$/i.test(p))
            || parts[0];
        normalized = cityPart || normalized;
    }

    return normalized.replace(/^(город|г\.|г)\s+/i, '').trim();
}

/** Объединяет город из шага подбора тарифа (shipmentCity) и адрес на шаге оформления (shipmentAddress). */
export function resolvePvzSearchLocation(form, name) {
    const address = {
        ...(form[`${name}City`] || {}),
        ...(form[`${name}Address`] || {}),
    };

    let city = address.city || address.cityName || address.settlement || '';
    city = normalizeCityLabel(city);
    if (!city && address.fullName) {
        city = normalizeCityLabel(String(address.fullName).split(',')[0]);
    }

    const postalCode = address.postalCode || address.estimatedPostalCode || '';

    return {
        postalCode: postalCode ? String(postalCode).trim() : '',
        city,
        centerLat: address.geoLat,
        centerLon: address.geoLon
    };
}

export function resolveParcelWeightForPvz(form) {
    const w = form.weight != null && form.weight !== ''
        ? form.weight
        : (form.cargoWeight != null && form.cargoWeight !== ''
            ? form.cargoWeight
            : form.documentWeight);
    if (w == null || w === '') {
        return null;
    }
    const num = Number(w);
    if (isNaN(num) || num <= 0) {
        return null;
    }
    return num;
}

export function getMapApiEntries(data) {
    return (data && (data.entries || data.Entries)) || [];
}



// WEBPACK FOOTER //
// ./App/pages/parcels/form_v3/functions/pvzAddress.js